import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, imgSrc } from "@/lib/api";

const TagPicker = ({ value = [], tags, onChange, name, single }) => {
  const [q, setQ] = useState("");
  const toggle = (k) => single ? onChange(k) : onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  const selected = single ? [value].filter(Boolean) : value;
  const groups = {};
  tags.filter((t) => !q || t.key.includes(q) || t.label.toLowerCase().includes(q.toLowerCase())).forEach((t) => (groups[t.group || "altro"] ||= []).push(t));
  return (
    <div className="border border-[#E6DFD5] bg-white p-3" data-testid={`field-${name}-tags`}>
      <Input placeholder="Cerca tag…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-xs mb-3" data-testid={`field-${name}-search`} />
      <div className="max-h-44 overflow-y-auto space-y-2">
        {Object.entries(groups).map(([g, list]) => (
          <div key={g} className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] uppercase tracking-widest text-[#8C8275] w-16">{g}</span>
            {list.map((t) => (
              <button type="button" key={t.key} onClick={() => toggle(t.key)} data-testid={`tag-chip-${name}-${t.key.replace(/[^a-z0-9]/gi, "-")}`}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${selected.includes(t.key) ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "border-[#E6DFD5] hover:border-[#C5A059]"}`}>
                {t.key}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const OptionsEditor = ({ value = [], tags, onChange }) => {
  const set = (i, patch) => onChange(value.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  return (
    <div className="space-y-3" data-testid="field-options-editor">
      {value.map((o, i) => (
        <div key={o.id || i} className="border border-[#E6DFD5] bg-white p-3 space-y-2">
          <div className="flex gap-2">
            <Input data-testid={`option-label-${i}`} placeholder="Testo risposta" value={o.label} onChange={(e) => set(i, { label: e.target.value })} />
            <Input data-testid={`option-hint-${i}`} placeholder="Nota (opz.)" value={o.hint || ""} onChange={(e) => set(i, { hint: e.target.value })} className="w-40" />
            <button type="button" data-testid={`option-remove-${i}`} onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="p-2 text-[#8C8275] hover:text-[#A0482F]"><Trash2 size={15} /></button>
          </div>
          <TagPicker name={`option-${i}`} value={o.tags || []} tags={tags} onChange={(t) => set(i, { tags: t })} />
        </div>
      ))}
      <button type="button" data-testid="option-add-btn" className="fs-btn-ghost" onClick={() => onChange([...value, { label: "", hint: "", tags: [] }])}><Plus size={13} /> Aggiungi opzione</button>
    </div>
  );
};

const ImageField = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-4 items-start">
      <div className="w-20 h-24 bg-[#F2ECE4] shrink-0 overflow-hidden">{value && <img src={imgSrc(value)} alt="" className="w-full h-full object-cover" data-testid="field-image-preview" />}</div>
      <div className="flex-1 space-y-2">
        <label className="fs-btn-ghost cursor-pointer" data-testid="field-image-upload-label">
          <Upload size={13} /> {busy ? "Caricamento…" : "Carica immagine"}
          <input type="file" accept="image/*" className="hidden" data-testid="field-image-upload-input" onChange={(e) => upload(e.target.files[0])} />
        </label>
        <Input data-testid="field-image-url" placeholder="oppure URL immagine" value={value || ""} onChange={(e) => onChange(e.target.value)} className="text-xs" />
      </div>
    </div>
  );
};

const ProductPicker = ({ value = [], products, onChange }) => (
  <div className="flex flex-wrap gap-1.5 border border-[#E6DFD5] bg-white p-3" data-testid="field-alternatives">
    {products.length ? products.map((p) => (
      <button type="button" key={p.id} data-testid={`alt-chip-${p.id}`} onClick={() => onChange(value.includes(p.id) ? value.filter((x) => x !== p.id) : [...value, p.id])}
        className={`text-xs px-2 py-1 rounded-full border ${value.includes(p.id) ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "border-[#E6DFD5]"}`}>
        {p.name} {value.includes(p.id) && <X size={10} className="inline" />}
      </button>
    )) : <span className="text-xs text-[#8C8275]">Nessun altro prodotto nello stesso step.</span>}
  </div>
);

export const Field = ({ name, label, type, value, onChange, tags, products, options, disabled }) => {
  const id = `field-${name}`;
  const inputs = {
    text: () => <Input id={id} data-testid={id} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} />,
    number: () => <Input id={id} data-testid={id} type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} />,
    textarea: () => <Textarea id={id} data-testid={id} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />,
    switch: () => <Switch id={id} data-testid={id} checked={!!value} onCheckedChange={onChange} />,
    select: () => (
      <select id={id} data-testid={id} disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full h-9 border border-input bg-white px-3 text-sm rounded-md">
        {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ),
    moments: () => (
      <div className="flex gap-3" data-testid={id}>
        {[["day", "Giorno"], ["night", "Sera"]].map(([k, l]) => {
          const on = (value || []).includes(k);
          return <button type="button" key={k} data-testid={`${id}-${k}`} onClick={() => onChange(on ? value.filter((x) => x !== k) : [...(value || []), k])}
            className={`px-4 py-1.5 text-xs rounded-full border ${on ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "border-[#E6DFD5] bg-white"}`}>{l}</button>;
        })}
      </div>
    ),
    tags: () => <TagPicker name={name} value={value} tags={tags} onChange={onChange} />,
    tag: () => <TagPicker name={name} value={value} tags={tags} onChange={onChange} single />,
    options: () => <OptionsEditor value={value} tags={tags} onChange={onChange} />,
    image: () => <ImageField value={value} onChange={onChange} />,
    products: () => <ProductPicker value={value} products={products} onChange={onChange} />,
  };
  const input = inputs[type]?.();
  return (
    <div className={type === "switch" ? "flex items-center justify-between" : ""}>
      <Label htmlFor={id} className="text-xs text-[#595652] block mb-2">{label}</Label>
      {input}
    </div>
  );
};
